import { useState, useEffect } from "react";
import { X, ChevronRight, ShieldCheck, Zap } from "lucide-react";
import { useWallet } from "@/context/WalletContext";

interface WalletConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WalletConnectModal({ isOpen, onClose }: WalletConnectModalProps) {
  const { connect, connected, publicKey } = useWallet();
  const [step, setStep] = useState<"intro" | "connecting" | "success">("intro");

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (connected) setStep("success");
      else setStep("intro");
    }
  }, [isOpen, connected]);

  if (!isOpen) return null;

  const handleConnect = async () => {
    setStep("connecting");
    try {
      await connect();
      setStep("success");
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setStep("intro");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {/* Modal Container */}
      <div className="relative flex w-full max-w-4xl max-h-[80vh] overflow-hidden rounded-2xl bg-[#0b0e11] border border-white/10 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/40 text-white/50 hover:bg-white/10 hover:text-white transition-colors"
        >
          <X className="size-5" />
        </button>

        {/* Left Column: Imagery & Branding (Hidden on mobile) */}
        <div className="hidden md:flex w-1/2 relative bg-gradient-to-br from-emerald-900/40 to-black overflow-hidden flex-col justify-end p-8">
          <div className="absolute inset-0 z-0">
            {/* The generated image from the task */}
            <img 
              src="/wallet_modal_hero.png" 
              alt="Crypto Trading Visual" 
              className="w-full h-full object-cover opacity-60 mix-blend-screen"
              onError={(e) => {
                // Fallback gradient if the image isn't placed in public dir yet
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-transparent to-neutral-900/50" />
          </div>
          
          <div className="relative z-10 space-y-4">
            <h2 className="text-3xl font-black text-white tracking-tight">Trade Faster.<br/>Trade Smarter.</h2>
            <p className="text-white/60 text-sm max-w-sm">
              Initialize a secure internal wallet to execute instant trades on Solana. Experience zero-latency execution directly from the chart, with keys staying locally on your device.
            </p>
            <div className="flex items-center gap-4 pt-4">
               <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-2 py-1 rounded">
                 <ShieldCheck className="size-3" /> Secure
               </div>
               <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-2 py-1 rounded">
                 <Zap className="size-3" /> Lightning Fast
               </div>
            </div>
          </div>
        </div>

        {/* Right Column: Connection Flow */}
        <div className="flex-1 p-8 md:p-12 flex flex-col justify-center relative">
          
          {step === "intro" && (
            <div className="space-y-8 animate-in slide-in-from-right-4 fade-in">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Initialize Wallet</h3>
                <p className="text-sm text-white/50">Create a secure internal wallet to start trading instantly with zero latency.</p>
              </div>

              <div className="space-y-3">
                <button 
                  onClick={handleConnect}
                  className="w-full flex items-center justify-between p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="size-10 rounded-lg bg-emerald-500/20 flex items-center justify-center p-2">
                      <ShieldCheck className="size-6 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-white text-base group-hover:text-emerald-400 transition-colors">Internal Trade Wallet</div>
                      <div className="text-xs text-white/40">Secure & Local</div>
                    </div>
                  </div>
                  <ChevronRight className="size-5 text-white/20 group-hover:text-emerald-400 transition-colors group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          )}

          {step === "connecting" && (
            <div className="flex flex-col items-center justify-center space-y-6 animate-in zoom-in-95 fade-in">
               <div className="relative">
                 <div className="size-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center p-3 animate-pulse">
                   <ShieldCheck className="size-8 text-emerald-400" />
                 </div>
                 <div className="absolute -inset-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
               </div>
               <div className="text-center">
                 <h3 className="text-lg font-bold text-white mb-1">Generating Secure Keys</h3>
                 <p className="text-sm text-white/50">Your keys are generated locally and never leave your browser.</p>
               </div>
            </div>
          )}

          {step === "success" && (
            <div className="flex flex-col items-center justify-center space-y-6 animate-in zoom-in-95 fade-in">
               <div className="size-16 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-500/40">
                 <ShieldCheck className="size-8 text-emerald-400" />
               </div>
               <div className="text-center">
                 <h3 className="text-lg font-bold text-white mb-1">Wallet Connected!</h3>
                 <p className="text-sm text-emerald-400/80 font-mono bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20 inline-block mt-2">
                   {publicKey?.slice(0,4)}...{publicKey?.slice(-4)}
                 </p>
               </div>
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
}
